import "@styles/globals.css";
import "@styles/toastify.css";

import { appWithTranslation } from "next-i18next";
import type { AppProps } from "next/app";
import {
  DehydratedState,
  HydrationBoundary,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { getClientSideQueryClient } from "@modules/api/queryClients";
import { NextPage } from "next";
import { ReactElement, ReactNode, useEffect } from "react";
import { volteFont, larsseitFont } from "@common/constants/localFonts";
import { User } from "@modules/user/types/user";
import { captureException, ErrorBoundary } from "@sentry/nextjs";
import userQueryKeys from "@modules/user/userQueryKeys";
import { getClientAppInsights } from "@modules/core/applicationInsights/clientTelemetryService";
import { IPageViewTelemetry } from "@microsoft/applicationinsights-web";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { ErrorFallback, ToastContainer } from "@talladega/components";
import usePreserveQueryParams from "@common/hooks/usePreserveQueryParams";
import nextI18NextConfig from "../../next-i18next.config";

type PageProps = {
  dehydratedState: DehydratedState;
};

type NextPageWithOptions = NextPage<{ dehydratedState: DehydratedState }> & {
  pageName: string;
  getLayout?: (page: ReactElement) => ReactNode;
  pageWrapper?: (page: ReactElement) => ReactElement;
};

type AppPropsWithOptions = AppProps & {
  Component: NextPageWithOptions;
  pageProps: PageProps;
};

const queryClient = getClientSideQueryClient();

const noOpWrapper = (page: ReactElement) => {
  return page;
};

const errorFallbackPage = () => <ErrorFallback />;

const App = ({ Component, pageProps, router }: AppPropsWithOptions) => {
  const {
    clientAppInsights,
    initialize: initializeAppInsights,
    reactPlugin,
  } = getClientAppInsights();

  const { pageName } = Component;

  // Ensure component's `pageName` is set so it doesn't change after build.
  // This is used by application insights for tracking page views.
  if (!pageName) {
    throw new Error("Component's `pageName` property is not set");
  }

  // AppInsights Setup
  useEffect(() => {
    initializeAppInsights();
  }, [initializeAppInsights]);

  useEffect(() => {
    const { route, query } = router;

    const properties: Record<string, string | string[] | undefined> = {
      route,
    };

    if (query) {
      Object.keys(query).forEach((key: string) => {
        properties[`query.${key}`] = query[key];
      });
    }

    const pageView: IPageViewTelemetry = {
      name: pageName,
      properties,
    };

    clientAppInsights?.trackPageView(pageView);
  }, [clientAppInsights, pageName, router]);

  usePreserveQueryParams([
    "/",
    "/confirm",
    "/about",
    "/faq",
    "/contact",
    "/appraisal/[...slug]",
  ]);

  const getLayout = Component.getLayout ?? noOpWrapper;
  const getWrapper = Component.pageWrapper ?? noOpWrapper;

  const getLoggedInUserId = (): string | null => {
    let LoggedInUserId = null;
    const user = queryClient.getQueryData(userQueryKeys.user()) as User;

    if (user && user.id) {
      LoggedInUserId = user.id;
    }
    return LoggedInUserId;
  };

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <ErrorBoundary
            beforeCapture={(scope) => {
              const loggedInUserId = getLoggedInUserId();

              if (loggedInUserId) {
                scope.setUser({ id: loggedInUserId });
              }
            }}
            onError={(error) => {
              captureException(error);
            }}
            fallback={errorFallbackPage}
          >
            <style jsx global>{`
              :root {
                --font-volte: ${volteFont.style.fontFamily};
                --font-larsseit: ${larsseitFont.style.fontFamily};
                --font-brother-1816: brother-1816, sans-serif;
              }
            `}</style>
            {getLayout(getWrapper(<Component {...pageProps} />))}
          </ErrorBoundary>
        </HydrationBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      <ToastContainer />
    </AppInsightsContext.Provider>
  );
};

export default appWithTranslation(App, nextI18NextConfig);
